import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch, useNavigate } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { z } from 'zod';
import type {
  GenerationSession,
  GenerationVersion,
  GenerationVersionSummary,
} from '@shared/schemas';
import { SessionWithTimelineSchema, GenerationVersionSchema } from '@shared/schemas';
import { apiGet } from '@/lib/api-client';

// Infer the SessionWithTimeline type from the schema to ensure consistency
type SessionWithTimeline = z.infer<typeof SessionWithTimelineSchema>;

/**
 * Search params for the /create route
 */
interface CreateSearchParams {
  session?: string;
  version?: string;
}

/**
 * Return type for useSession hook
 */
export interface UseSessionReturn {
  // URL state
  sessionId: string | undefined;
  selectedVersionId: string | undefined;

  // Data
  session: GenerationSession | null;
  versions: GenerationVersionSummary[];
  currentVersion: GenerationVersion | null;
  selectedVersion: GenerationVersionSummary | null;
  selectedVersionFull: GenerationVersion | null;

  // Computed
  isEditingOldVersion: boolean;
  nextVersionNumber: number;
  /**
   * The effective version ID being edited (selected or current).
   * Use this when determining which version to edit FROM in generation calls.
   * Will be undefined only when there's no session (first generation).
   */
  effectiveVersionId: string | null;

  // Loading
  isLoading: boolean;
  isRefetching: boolean;
  isLoadingVersion: boolean;

  // Actions
  switchVersion: (versionId: string) => void;
  backToCurrent: () => void;
  clearSession: () => void;
  refetch: () => void;
}

/**
 * Query key factory for session-related queries
 */
export const sessionKeys = {
  all: ['sessions'] as const,
  detail: (id: string) => ['sessions', id] as const,
  timeline: (id: string) => ['sessions', id, 'timeline'] as const,
  version: (sessionId: string, versionId: string) => ['sessions', sessionId, 'versions', versionId] as const,
};

/**
 * Fetch session with timeline from API
 */
async function fetchSessionTimeline(sessionId: string): Promise<SessionWithTimeline> {
  const response = await apiGet(
    `/sessions/${sessionId}/timeline`,
    SessionWithTimelineSchema
  );
  // Cast the response data to our local type to handle any schema inference differences
  return response.data as unknown as SessionWithTimeline;
}

/**
 * Fetch full version details from API
 */
async function fetchVersion(sessionId: string, versionId: string): Promise<GenerationVersion> {
  const response = await apiGet(
    `/sessions/${sessionId}/versions/${versionId}`,
    GenerationVersionSchema
  );
  // Cast to handle Zod schema input/output type mismatch
  return response.data as unknown as GenerationVersion;
}

/**
 * Hook for managing session state in the Version History feature.
 *
 * Provides:
 * - URL-based session and version state (via TanStack Router)
 * - Session data with version timeline (via TanStack Query)
 * - Navigation actions for version switching
 *
 * @example
 * ```tsx
 * const {
 *   session,
 *   versions,
 *   selectedVersion,
 *   isEditingOldVersion,
 *   switchVersion,
 *   backToCurrent,
 * } = useSession();
 *
 * // Switch to an older version
 * switchVersion('version-uuid');
 *
 * // Go back to current/latest version
 * backToCurrent();
 * ```
 */
export function useSession(): UseSessionReturn {
  const navigate = useNavigate();

  // Get URL search params using TanStack Router
  // The /create route validates search params via createSearchSchema in routeTree.gen.ts
  const searchParams = useSearch({ from: '/create' }) as CreateSearchParams;

  const sessionId = searchParams.session;
  const selectedVersionId = searchParams.version;

  // Fetch session with timeline when sessionId exists
  const {
    data: sessionData,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery({
    queryKey: sessionKeys.timeline(sessionId ?? ''),
    queryFn: () => fetchSessionTimeline(sessionId!),
    enabled: !!sessionId,
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: false,
  });

  // Fetch full version details when a specific version is selected
  // This is needed because GenerationVersionSummary doesn't include code
  const {
    data: selectedVersionFull,
    isLoading: isLoadingVersion,
  } = useQuery({
    queryKey: sessionKeys.version(sessionId ?? '', selectedVersionId ?? ''),
    queryFn: () => fetchVersion(sessionId!, selectedVersionId!),
    enabled: !!sessionId && !!selectedVersionId,
    staleTime: 1000 * 60 * 5, // 5 minutes - versions are immutable
    refetchOnWindowFocus: false,
  });

  // Extract session and current version from response
  const session: GenerationSession | null = sessionData ?? null;
  const currentVersion: GenerationVersion | null = sessionData?.currentVersion ?? null;

  // Sort versions in ascending order by versionNumber for timeline display
  const versions: GenerationVersionSummary[] = useMemo(() => {
    if (!sessionData?.versions) return [];
    return [...sessionData.versions].sort((a, b) => a.versionNumber - b.versionNumber);
  }, [sessionData?.versions]);

  // Find selected version (from URL or fall back to current)
  const selectedVersion: GenerationVersionSummary | null = useMemo(() => {
    if (!versions.length) return null;

    // If URL has a specific version selected, use that
    if (selectedVersionId) {
      const found = versions.find((v) => v.id === selectedVersionId);
      if (found) return found;
    }

    // Fall back to current version
    if (session?.currentVersionId) {
      return versions.find((v) => v.id === session.currentVersionId) ?? null;
    }

    // Fall back to latest version (highest version number)
    return versions[versions.length - 1] ?? null;
  }, [versions, selectedVersionId, session?.currentVersionId]);

  // Computed: are we editing an old version?
  const isEditingOldVersion = useMemo(() => {
    if (!selectedVersion || !session?.currentVersionId) return false;
    return selectedVersion.id !== session.currentVersionId;
  }, [selectedVersion, session?.currentVersionId]);

  // Computed: next version number (current + 1)
  const nextVersionNumber = useMemo(() => {
    if (!session?.versionCount) return 1;
    return session.versionCount + 1;
  }, [session?.versionCount]);

  // Computed: effective version ID to edit from (selected > current > null)
  // This is the version that will be used as the parent for the next generation
  const effectiveVersionId = useMemo(() => {
    return selectedVersionId ?? session?.currentVersionId ?? null;
  }, [selectedVersionId, session?.currentVersionId]);

  // Action: switch to a different version
  const switchVersion = useCallback(
    (versionId: string) => {
      if (!sessionId) return;

      navigate({
        to: '/create',
        search: {
          session: sessionId,
          version: versionId,
        },
      });
    },
    [navigate, sessionId]
  );

  // Action: go back to current (latest) version
  const backToCurrent = useCallback(() => {
    if (!sessionId || !session?.currentVersionId) return;

    navigate({
      to: '/create',
      search: {
        session: sessionId,
        // Remove version param to use current
        version: undefined,
      },
    });
  }, [navigate, sessionId, session?.currentVersionId]);

  // Action: clear session (for new generation)
  const clearSession = useCallback(() => {
    navigate({
      to: '/create',
      search: {},
    });
  }, [navigate]);

  // Wrapper for refetch to match expected signature
  const handleRefetch = useCallback(() => {
    refetch();
  }, [refetch]);

  return {
    // URL state
    sessionId,
    selectedVersionId,

    // Data
    session,
    versions,
    currentVersion,
    selectedVersion,
    selectedVersionFull: selectedVersionFull ?? null,

    // Computed
    isEditingOldVersion,
    nextVersionNumber,
    effectiveVersionId,

    // Loading
    isLoading,
    isRefetching,
    isLoadingVersion,

    // Actions
    switchVersion,
    backToCurrent,
    clearSession,
    refetch: handleRefetch,
  };
}

/**
 * Hook to invalidate session queries after mutations
 */
export function useInvalidateSession() {
  const queryClient = useQueryClient();

  return useCallback(
    (sessionId?: string) => {
      if (sessionId) {
        // Invalidate specific session
        queryClient.invalidateQueries({ queryKey: sessionKeys.timeline(sessionId) });
        queryClient.invalidateQueries({ queryKey: sessionKeys.detail(sessionId) });
      } else {
        // Invalidate all session queries
        queryClient.invalidateQueries({ queryKey: sessionKeys.all });
      }
    },
    [queryClient]
  );
}

/**
 * Hook to prefetch session timeline (for hover previews)
 */
export function usePrefetchSession() {
  const queryClient = useQueryClient();

  return useCallback(
    (sessionId: string) => {
      queryClient.prefetchQuery({
        queryKey: sessionKeys.timeline(sessionId),
        queryFn: () => fetchSessionTimeline(sessionId),
        staleTime: 1000 * 60 * 2, // 2 minutes
      });
    },
    [queryClient]
  );
}
